import isEmpty from 'lodash-es/isEmpty';
import every from 'lodash-es/every';
import findKey from 'lodash-es/findKey';
import omit from 'lodash-es/omit';

import { environment as ENV } from '@environment';
import { isObject, isNumber, isNullOrUndefined } from '@app/util/util';

export interface PercentagesValues {
  total: number;
  card: number;
  credits: number;
  references: (number | string)[];
  bannerImage?: {
    mobile?: string;
    desktop?: string;
  };
}

export interface PercentagesBanner extends PercentagesValues {
  product_cashback?: { [key: string]: PercentagesValues };
}

export enum BannerResultsType {
  CANCELLATION = 'CANCELLATION',
  RAPPI_CARD = 'RAPPI_CARD',
  CHUBB_BANNER = 'CHUBB_BANNER',
  PRIME = 'PRIME'
}

export enum BannerResultsContainer {
  FLIGHTS = 'FLIGHTS',
  AUTOS = 'AUTOS',
  HOTELS = 'HOTELS',
  AIR_PACKAGES = 'AIR_PACKAGES',
  BUSES = 'BUSES'
}

export const FLIGHTS_BANNER_ENABLED = !!ENV.countryModules?.banner_results?.banner_enabled_flighs;

// eslint-disable-next-line max-len
export const FLIGHTS_BANNER_AVAILABLE: BannerResultsType = ENV.countryModules?.banner_results?.banner_flights_available as BannerResultsType;

export const FLIGHTS_BANNER_PERCENTAGES: PercentagesBanner = ENV.countryModules?.banner_results?.cashback?.flights;

export const HOTELS_BANNER_ENABLED = !!ENV.countryModules?.banner_results?.banner_enabled_hotels;

// eslint-disable-next-line max-len
export const HOTELS_BANNER_AVAILABLE: BannerResultsType = ENV.countryModules?.banner_results?.banner_hotels_available as BannerResultsType;

export const BYHOURS_BANNER_AVAILABLE: 
  BannerResultsType = ENV.countryModules?.banner_results?.banner_byhours_available as BannerResultsType;

export const HOTELS_BANNER_PERCENTAGES: PercentagesBanner = ENV.countryModules?.banner_results?.cashback?.hotels;

export const AUTOS_BANNER_ENABLED = !!ENV.countryModules?.banner_results?.banner_enabled_autos;

// eslint-disable-next-line max-len
export const AUTOS_BANNER_AVAILABLE: BannerResultsType = ENV.countryModules?.banner_results?.banner_autos_available as BannerResultsType;

export const AUTOS_BANNER_PERCENTAGES: PercentagesBanner = ENV.countryModules?.banner_results?.cashback?.autos;

export const AIR_PACKAGES_BANNER_ENABLED = !!ENV.countryModules?.banner_results?.banner_enabled_air_packages;

// eslint-disable-next-line max-len
export const AIR_PACKAGES_BANNER_AVAILABLE: BannerResultsType = ENV.countryModules?.banner_results?.banner_air_packages_available as BannerResultsType;

export const AIR_PACKAGES_BANNER_PERCENTAGES: PercentagesBanner = ENV.countryModules?.banner_results?.cashback?.air_packages;

export const BUSES_BANNER_ENABLED = !!ENV.countryModules?.banner_results?.banner_enabled_buses;

// eslint-disable-next-line max-len
export const BUSES_BANNER_AVAILABLE: BannerResultsType = ENV.countryModules?.banner_results?.banner_buses_available as BannerResultsType;

export const BUSES_BANNER_PERCENTAGES: PercentagesBanner = ENV.countryModules?.banner_results?.cashback?.buses;

export function bannerIsValidToDisplay(enabled: boolean, type: BannerResultsType): boolean {
  return enabled && Object.values(BannerResultsType).includes(type);
}

export function showCancellationBanner(container: BannerResultsContainer): boolean {
  return container === BannerResultsContainer.FLIGHTS;
}

export function isActivateRappiCardCashback(
  enabled: boolean, percentages: PercentagesBanner): boolean {
  const banner = omit(percentages, 'bannerImage');
  return enabled &&
    !isEmpty(banner) && every(banner, (item) => {
    return Array.isArray(item) ? !isEmpty(item) :
      isObject(item) ? isActivateRappiCardCashback(enabled, item as PercentagesBanner) :
        isNumber(item) ? item > 0 : false;
  });
}

export function hasRappiCardCashbackMapper(
  enabled: boolean, percentages: PercentagesBanner, productReference: number | string): boolean {
  return isActivateRappiCardCashback(enabled, percentages) && isActivateOfferByRef(productReference, percentages);
}

export function getBannerOfferPercentage(
  percentages: PercentagesBanner, productReference: number | string, code: string
): number {
  if (isActivateOfferByRef(productReference, percentages)) {
    const general = percentages?.total || null;
    const referenceKey: string = findKey(percentages?.product_cashback, (offer: PercentagesValues) =>
      isByProductOffer(productReference, offer.references));
    if (referenceKey && (isEmpty(code) || (code && code === referenceKey))) {
      const product = percentages?.product_cashback?.[referenceKey];
      return isByProductOffer(productReference, product?.references) ? (product.total || null) : general;
    } else return general;
  } else return null;
}

function isActivateOfferByRef(productReference: number | string, percentages: PercentagesBanner): boolean {
  const productOffers = percentages?.product_cashback || {};
  const allProductReferences = Object.values(productOffers)?.map(offer => offer.references);
  const references = [].concat(percentages?.references).concat(...allProductReferences).filter(ref => !!ref);
  return !!references && !!productReference && isByProductOffer(productReference, references);
}

function isByProductOffer(productReference: number | string, references: (number | string)[]): boolean {
  return !isNullOrUndefined(
    references?.find(ref => {
      if (typeof ref === 'string' && typeof productReference === 'number') {
        const refItems = ref.split('_');
        const reference = refItems.pop();
        return Number(reference) === productReference;
      } else if (typeof ref === 'string' && typeof productReference === 'string') {
        const refItems = ref.split('_');
        const productItems = productReference.split('_');
        const productRef = productItems.pop();
        const reference = refItems.pop();
        return Number(reference) === Number(productRef);
      } else if (typeof ref === 'number' && typeof productReference === 'number') {
        return ref === productReference;
      } else if (typeof ref === 'number' && typeof productReference === 'string') {
        const productItems = productReference.split('_');
        const productRef = productItems.pop();
        return ref.toString() === productRef;
      }
    }));
}

